import { Component, OnInit, Output, EventEmitter, Input, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Pagination, API, AreaDTO } from 'src/app/services/api/api';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-area-selection',
  templateUrl: './sb-area-selection.component.html',
  styleUrls: ['./sb-area-selection.component.scss'],
  standalone: false,
})
export class SbAreaSelectionComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  results: AreaDTO[] = [];

  loading = false;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  searchStr: string;
  search: string;
  exactMatch: boolean = false;

  maxSize: number;
  boundaryLinks: boolean = true;

  resultCount: number;
  currentCount: number;
  collectionSize: number;

  pagination: BehaviorSubject<Pagination>;

  paginationString: string;

  _page: number = 1;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.pagination.value.page = val;
  }

  sizes: number[] = [5, 8, 10, 15, 20, 25];

  paginationInitialized: boolean = false;

  @Input()
  selectedAreas: AreaDTO[];

  @Output()
  areaSelected: EventEmitter<AreaDTO> = new EventEmitter<AreaDTO>();

  @Output()
  areaDeselected: EventEmitter<AreaDTO> = new EventEmitter<AreaDTO>();

  constructor(
    private readonly api: API,
    private readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService
  ) {}

  ngOnInit() {
    this.maxSize = 5;
    this.initPagination(false);
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.loading = true;

    this.api
      .dm_geo_Area_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: data => {
          this.results = data;
          this.currentCount = data.length;
          this.paginationString = this.stringTransformation.getPaginationString(
            this.page,
            this.maxSize,
            this.currentCount,
            this.resultCount,
            'items'
          );
          this.paginationInitialized = true;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  dosearch($event) {
    this.search = $event;
    this.requestDebounced();
  }

  initPagination(doRequest: boolean = true) {
    this.pagination = new BehaviorSubject<Pagination>({
      page: this.page,
      count: this.maxSize ? this.maxSize : 8,
      pages: -1,
      resultcount: -1,
    });
    this.pagination.subscribe((e: Pagination) => {
      this.collectionSize = e.resultcount;
      this.maxSize = e.count;
      this.resultCount = e.resultcount;
      // this.page = e.page;
    });

    if (doRequest) {
      this.requestDebounced();
    }
  }

  setMaxSize(size) {
    this.maxSize = size;
    this.pagination.value.count = size;
    this.requestDebounced();
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  collapseToggled($event) {
    if (!$event) {
      this.requestDebounced();
    }
  }

  getClass(area: AreaDTO) {
    if (this.selectedAreas.findIndex(value => value.id === area.id) >= 0) {
      return 'cursor-remove selected';
    }

    return 'cursor-add';
  }

  toggle(item: AreaDTO) {
    if (this.selectedAreas.findIndex(value => value.id === item.id) >= 0) {
      this.areaDeselected.emit(item);
    } else {
      this.areaSelected.emit(item);
    }
  }

  getName(item: AreaDTO) {
    const hasName: boolean = item.name ? item.name.length > 0 : false;
    if (hasName) {
      return item.name + ' ' + '(ID ' + item.id + ')';
    } else {
      return 'ID ' + item.id;
    }
  }

  getDescription(item: AreaDTO) {
    return this.stringTransformation.addressToFullString(item.address);
  }
}
